import React from "react";
import { ErrorMessage, Field, Formik, Form } from "formik";
import {
  AddButton,
  CancelButton,
  FormLabel,
  RequiredIndicator,
} from "assets/js/Styles";
import { Col, Row, Spinner } from "reactstrap";
import classNames from "classnames";
import FormGenerator from "components/customs/form-generator/input";
import { VIEW_ACTION } from "constants/actions";
import styled from "styled-components";
import CustomSelect from "components/customs/dropdown";
import { addProjectDropdownStyles } from "constants/style";
import { NCR_REGION_ID } from "utility/constants";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import AddCourseTable from "./Table";

const FormSection = styled.div`
  margin-top: 4rem;
`;

const RowSection = styled(Row)`
  margin-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  margin-left: 0;
  margin-right: 0;
`;

const FormColRight = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    width: 50%;
  }

  .dropzone-container {
    width: auto;
    height: 65px;
    border: 1px dashed #a3a3a3;

    .upload-files {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
`;

const StyledAddButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const StyledCancelButton = styled(CancelButton)`
  width: 130px;
`;

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
  gap: 16px;
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  regionId: Yup.object().required("Required"),
  provinceId: Yup.object().required("Required"),
  municipalityId: Yup.object().required("Required"),
  contactNumber: Yup.string()
    .nullable()
    .transform((value) => value || null)
    .test(
      "phone",
      "Invalid format (must be 11 digits starting with 09)",
      (value) => !value || (/^(09)\d{9}$/.test(value) && value.length === 11)
    ),
});

const TVISettingsFormSection = ({
  form,
  setForm,
  action,
  isLoading,
  handleSubmit,
  regionOptions,
  provinceOptions,
  municipalityOptions,
  coursesOptions,
  setSelectedCourses,
  selectedCourses,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={form}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <FormSection>
            <Field name="name">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"TVI Name"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the name of the TVI"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="text"
                      name="name"
                      placeholder="TVI Name"
                      value={values.name}
                      isDisabled={action === VIEW_ACTION}
                      className={classNames({
                        "is-invalid": errors?.name && touched?.name,
                      })}
                      onChange={(event) => {
                        const {
                          target: { value },
                        } = event;

                        setFieldValue("name", value);

                        setForm((prevForm) => ({
                          ...prevForm,
                          name: value,
                        }));
                      }}
                    />
                    <ErrorMessage name="name">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="regionId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Region"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the region of the TVI"}</p>
                  </Col>
                  <FormColRight>
                    <CustomSelect
                      placeholder="Region"
                      name="regionId"
                      styles={addProjectDropdownStyles}
                      options={regionOptions}
                      value={values.regionId}
                      className={classNames({
                        "is-invalid": errors?.regionId && touched?.regionId,
                      })}
                      onChange={(option) => {
                        setFieldValue("regionId", option);

                        setForm((prevForm) => ({
                          ...prevForm,
                          regionId: option,
                        }));
                      }}
                      isDisabled={action === VIEW_ACTION}
                    />
                    <ErrorMessage name="regionId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="provinceId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Province"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the province where it is offered"}</p>
                  </Col>
                  <FormColRight>
                    <CustomSelect
                      placeholder="Province"
                      name="provinceId"
                      styles={addProjectDropdownStyles}
                      options={provinceOptions}
                      value={values.provinceId}
                      className={classNames({
                        "is-invalid": errors?.provinceId && touched?.provinceId,
                      })}
                      onChange={(option) => {
                        setFieldValue("provinceId", option);

                        setForm((prevForm) => ({
                          ...prevForm,
                          provinceId: option,
                        }));
                      }}
                      isDisabled={action === VIEW_ACTION}
                    />
                    <ErrorMessage name="provinceId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="municipalityId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Municipality"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the municipality where it is offered"}</p>
                  </Col>
                  <FormColRight>
                    <CustomSelect
                      placeholder="Municipality"
                      name="municipalityId"
                      styles={addProjectDropdownStyles}
                      options={municipalityOptions}
                      value={values.municipalityId}
                      className={classNames({
                        "is-invalid":
                          errors?.municipalityId && touched?.municipalityId,
                      })}
                      onChange={(option) => {
                        setFieldValue("municipalityId", option);

                        setForm((prevForm) => ({
                          ...prevForm,
                          municipalityId: option,
                        }));
                      }}
                      isDisabled={
                        action === VIEW_ACTION ||
                        values.regionId?.value === NCR_REGION_ID
                      }
                    />
                    <ErrorMessage name="municipalityId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="contactNumber">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>{"Contact Number"}</FormLabel>
                    <p>{"Provide the contact number"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="mobile"
                      name="contactNumber"
                      placeholder="Contact Number"
                      value={values.contactNumber}
                      className={classNames({
                        "is-invalid":
                          errors?.contactNumber && touched?.contactNumber,
                      })}
                      onChange={(value) => {
                        setFieldValue("contactNumber", value);

                        setForm((prevForm) => ({
                          ...prevForm,
                          contactNumber: value,
                        }));
                      }}
                      isDisabled={action === VIEW_ACTION}
                    />
                    <ErrorMessage name="contactNumber">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>
          </FormSection>

          {form?.provinceId && (
            <AddCourseTable
              data={coursesOptions}
              setCourses={setSelectedCourses}
              courses={selectedCourses}
              action={action}
            />
          )}
          <BtnSection>
            <StyledCancelButton
              outline
              type="button"
              disabled={isLoading}
              onClick={() => navigate(-1)}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledAddButton
              color="primary"
              type="submit"
              disabled={isLoading || action === VIEW_ACTION}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledAddButton>
          </BtnSection>
        </Form>
      )}
    </Formik>
  );
};

export default TVISettingsFormSection;
